/**
 * Connect 4 as **data**. There is no game logic in this file and none anywhere
 * else in `src/games/connect4/` — `rules.ts` is a thin adapter over the shared
 * interpreter, which executes this literal.
 *
 * `as const satisfies GameSpec` is load-bearing twice over: it checks the
 * literal against the schema at compile time, and it keeps the literal types
 * (`seats: readonly [1, 2]`, `length: 4`) so this game's own `Seat` and
 * `Status['line']` stay exact — `1 | 2` and a readonly 4-tuple — throughout
 * `rules.ts`, its test suite and the client's renderer.
 *
 * Those narrow types stop at the socket. The wire is spec-driven and carries a
 * seat as `number` and a line as `readonly number[]`, because a platform whose
 * games arrive as data cannot know either statically. `wire.ts` in the client
 * is the boundary that converts back, validated at runtime against this spec.
 */
import { parseGameSpec } from '../spec/schema.ts';
import type { GameSpec } from '../spec/schema.ts';

export const CONNECT4_SPEC = {
  id: 'connect4',
  specVersion: 1,

  rules: {
    seats: [1, 2],
    turnOrder: { kind: 'cyclic' },
    board: { kind: 'grid', cols: 7, rows: 6, index: 'row_major', rowZero: 'bottom' },
    cells: { empty: 0, occupancy: 'seat', mutation: 'write_once' },
    moves: [{ id: 'drop', kind: 'drop', param: 'column', along: 'column', fill: 'ascending' }],
    terminal: {
      win: [
        {
          kind: 'line',
          length: 4,
          through: 'last_placement',
          // [dRow, dCol], scanned in this order: horizontal, vertical, "/", "\".
          directions: [
            [0, 1],
            [1, 0],
            [1, 1],
            [1, -1],
          ],
        },
      ],
      draw: [{ kind: 'board_full' }],
    },
    encoding: { kind: 'digits' },
  },

  components: {
    title: 'Connect 4',
    playerCountLabel: '2 players',
    board: {
      grid: { cols: 7, rows: 6 },
      cellShape: 'circle',
      cellDiameterMm: 40,
      cellSpacingMm: 6,
      frameThicknessMm: 12,
      sheetSizeMm: { w: 420, h: 297 },
      bleedMm: 3,
      material: 'card',
    },
    pieces: [
      {
        id: 'disc-seat-1',
        seatRef: 1,
        label: 'Amber disc',
        count: 21,
        shape: 'disc',
        diameterMm: 38,
        thicknessMm: 3,
        colorHex: '#f5a524',
      },
      {
        id: 'disc-seat-2',
        seatRef: 2,
        label: 'Cyan disc',
        count: 21,
        shape: 'disc',
        diameterMm: 38,
        thicknessMm: 3,
        colorHex: '#3ec9d6',
      },
    ],
    rulesSummaryText:
      'Players take turns dropping a disc into one of seven columns. A disc falls to the ' +
      'lowest free space in that column. The first player to line up four of their own discs ' +
      'horizontally, vertically or diagonally wins. If the grid fills with no such line, the ' +
      'game is a draw.',
  },

  presentation: {
    theme: {
      canvas: '#0b0d12',
      board: '#161b26',
      // Amber against cyan stays separable under protanopia; `marker` carries the
      // same information without colour (WCAG 1.4.1).
      seats: {
        '1': { color: '#f5a524', marker: 'filled' },
        '2': { color: '#3ec9d6', marker: 'ring' },
      },
    },
    layout: { renderRowZeroAt: 'bottom', interactiveUnit: 'column', landingPreview: 'ghost' },
    motion: { dropMs: 260, easing: 'gravity', respectsReducedMotion: true },
    labels: {
      cellTemplate: 'row {row}, column {col}, {occupant}',
      occupant: { empty: 'empty', own: 'your disc', other: 'opponent disc' },
      statuses: { yourTurn: 'Your turn', theirTurn: 'Waiting for your opponent' },
    },
  },
} as const satisfies GameSpec;

/**
 * Load-time proof that the literal above still satisfies every runtime
 * refinement, not merely the type. `satisfies` cannot see `superRefine`, so
 * without this a drift in, say, piece counts would ship silently.
 */
const validation = parseGameSpec(CONNECT4_SPEC);
if (!validation.ok) {
  throw new Error(
    `CONNECT4_SPEC is invalid: ${validation.issues
      .map((issue) => `${issue.path}: ${issue.message}`)
      .join('; ')}`,
  );
}
